import React, { useState } from 'react';
import { X } from 'lucide-react';

const MovementModal = ({ assetId, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    movement_type: 'rental_out',
    from_location: 'Gudang Utama',
    to_location: '',
    client_id: '',
    rental_id: '',
    notes: '',
    status: ''
  });
  const [loading, setLoading] = useState(false);

  const movementTypes = [
    { value: 'rental_out', label: '📤 Rental Out (Keluar untuk Disewa)' },
    { value: 'rental_return', label: '📥 Rental Return (Kembali dari Sewa)' },
    { value: 'maintenance_in', label: '🔧 Maintenance In (Masuk Perbaikan)' },
    { value: 'maintenance_out', label: '✅ Maintenance Out (Selesai Perbaikan)' },
    { value: 'transfer', label: '🔄 Transfer (Pindah Lokasi)' },
    { value: 'demo', label: '🎯 Demo (Keluar untuk Demo)' },
    { value: 'internal_use', label: '💼 Internal Use (Digunakan Internal)' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Prepare data
      const submitData = {
        ...formData,
        // Auto-set status based on movement type
        status: getStatusFromMovementType(formData.movement_type)
      };

      // Call API (will be imported from services/api.js)
      const { assetsAPI } = await import('../services/api');
      await assetsAPI.addMovement(assetId, submitData);

      alert('✅ Perpindahan berhasil dicatat!');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error recording movement:', error);
      alert('❌ Gagal mencatat perpindahan: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const getStatusFromMovementType = (type) => {
    const statusMap = {
      rental_out: 'rented',
      rental_return: 'ready',
      maintenance_in: 'maintenance',
      maintenance_out: 'ready',
      transfer: '', // Keep current status
      demo: 'on_duty',
      internal_use: 'on_duty'
    };
    return statusMap[type] || '';
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="glass-card p-6 rounded-xl border border-border/40 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-foreground">Catat Perpindahan Asset</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Movement Type */}
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-2">
              Jenis Perpindahan <span className="text-destructive">*</span>
            </label>
            <select
              value={formData.movement_type}
              onChange={(e) => setFormData({ ...formData, movement_type: e.target.value })}
              className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
              required
            >
              {movementTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* From Location */}
            <div>
              <label className="block text-sm font-medium text-foreground/80 mb-2">
                Dari Lokasi <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                value={formData.from_location}
                onChange={(e) => setFormData({ ...formData, from_location: e.target.value })}
                className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                placeholder="Gudang Utama"
                required
              />
            </div>

            {/* To Location */}
            <div>
              <label className="block text-sm font-medium text-foreground/80 mb-2">
                Ke Lokasi <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                value={formData.to_location}
                onChange={(e) => setFormData({ ...formData, to_location: e.target.value })}
                className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors"
                placeholder="PT. ABC / Cabang Jakarta / dll"
                required
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-2">
              Catatan
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-4 py-3 bg-muted/50 border border-border rounded-lg text-foreground focus:outline-none focus:border-primary transition-colors resize-none"
              rows="3"
              placeholder="Catatan tambahan tentang perpindahan asset..."
            />
          </div>

          {/* Info */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
            <p className="text-sm text-blue-600">
              💡 <strong>Status asset akan otomatis diupdate</strong> berdasarkan jenis perpindahan yang dipilih.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors"
              disabled={loading}
            >
              Batal
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Menyimpan...' : 'Catat Perpindahan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MovementModal;
